import { VehiclePossibility } from '../interfaces/interface.vehicle';
import { fetchDealerInfo } from '../services/service.dealer';
import EventBus from '../services/service.eventBus';
import {
  fetchColors,
  fetchDrivetrains,
  fetchEngines,
  fetchOptions,
  fetchOptionsChanges,
  fetchTransmissions,
  fetchVehicle,
  fetchVehiclePossibilities,
} from '../services/service.vehicle';
import { fetchVehicleEligibility } from '../services/services.eligibility';
import { resetAll } from '../store/store.all';
import {
  state as answerState,
  getFromLocal as getAnswersFromLocal,
  populateFromLocal as populateAnswersFromLocal,
  setInitialized as setAnswersInitialized,
} from '../store/store.answers';
import { state as dynamicEntryState } from '../store/store.dynamic-entry';
import { state as vehicleConfigState } from '../store/store.vehicle-config';
import { convertOptionsToRequestString, filterFalseOptions, filterTrueOptions, mapColorNameToHex } from '../utils/utils.vehicle';

import { clearAnswers, onStateFieldAnswered, onVehicleOptionsAnswered, setAnswersFromVehicle } from './actions.answers';

export const onVehicleConfigInit = async (
  yearId: number,
  makeId: number,
  modelId: number,
  trimId: number,
  colorId: number,
  vin: string = '',
  dealerId: number,
  selectedOptions: number[] = []
) => {
  if (!yearId || !makeId || !modelId || !trimId) {
    return;
  }

  let dealerInfo;
  if (dealerId) {
    dealerInfo = await onGetVehicleDealerInfo(dealerId);
  }

  // check if there is browser storage and if trim is the same
  if (hasBrowserStorage() && hasSameTrimInStorage(trimId) && (!vehicleConfigState.trimId || vehicleConfigState.trimId === trimId)) {
    // if there is something in browser store and same trim then populate from local
    populateAnswersFromLocal();
    setAnswersInitialized(true);

    await getVehicleConfig(yearId, makeId, modelId, trimId, answerState.vehicle.colorId, vin, answerState.vehicle.apiSelectedOptionIds);
    updateTedFromAnswers();
  } else {
    console.log('Clearing saved answers and resetting stores on vehicle config initialization');
    clearAnswers();
    resetAll();

    await getVehicleConfig(yearId, makeId, modelId, trimId, colorId, vin, selectedOptions);

    // create store
    setAnswersInitialized(true);
    setAnswersFromVehicle(vehicleConfigState, dealerInfo);
  }
};

const getVehicleConfig = async (yearId, makeId, modelId, trimId, colorId, vin, selectedOptions = []) => {
  onGetVehicleEligibility(trimId);
  // make api call to get vehicle config
  if (vin.length === 0) {
    // YMMT entry, so don't look up the VIN possibilities
    await onYMMTVehicleInit(yearId, makeId, modelId, trimId, colorId, selectedOptions);
  } else {
    // VIN entry, so look up the VIN possibilities
    const possibleVehicles = await onVehiclePossibilitiesFetch(vin);
    const foundVehicle = possibleVehicles.find((v) => v.trim.id === trimId);
    console.log('getVehicleConfig', { yearId, makeId, modelId, trimId, colorId, vin, selectedOptions, possibleVehicles, foundVehicle });
    if (foundVehicle) {
      await onVINVehicleConfigInit(foundVehicle, vin, colorId, selectedOptions);
    }
  }
  if (selectedOptions.length > 0) {
    const selectedOptionsMapped = selectedOptions.map((optionId) => {
      return {
        optionId,
        isSelected: true,
      };
    });

    await onVehicleUserSelectedBatch(trimId, selectedOptionsMapped);
  }
};

const hasBrowserStorage = () => {
  try {
    getAnswersFromLocal();
    return true;
  } catch (error) {
    // does not have browser storage
    return false;
  }
};

const onGetVehicleEligibility = async (trimId) => {
  const vehicleEligibilityCheck = await fetchVehicleEligibility(trimId);
  vehicleConfigState.excludedTransmissions = vehicleEligibilityCheck.excludedTransmissions;
  vehicleConfigState.excludedEngines = vehicleEligibilityCheck.excludedEngines;
  vehicleConfigState.excludedDrivetrains = vehicleEligibilityCheck.excludedDrivetrains;
};

const onGetVehicleDealerInfo = async (dealerId) => {
  const dealerResponse = await fetchDealerInfo(dealerId);
  const dealerInfo = extractDealerInfo(dealerResponse);
  return { dealerId, dealershipName: dealerInfo['displayName'], dealerGuid: dealerInfo['dealerGuid'] };
};

const extractDealerInfo = (rawResponse): string => {
  try {
    if (rawResponse['data']) {
      return rawResponse['data'];
    }
  } catch (err) {
    console.error('Error extractDealerInfo', err);
    return null;
  }
};

const onYMMTVehicleInit = async (yearId: number = 0, makeId: number = 0, modelId: number = 0, trimId: number = 0, colorId: number = 0, selectedOptions: number[] = []) => {
  await onVehicleFetch(yearId, makeId, modelId, trimId);
  await Promise.all([
    onVehicleColorFetch(trimId, colorId),
    onVehicleEngineFetch(trimId),
    onVehicleTransmissionFetch(trimId),
    onVehicleDrivetrainFetch(trimId),
    onVehicleOptionsFetch(trimId, selectedOptions),
  ]);
};

const onVINVehicleConfigInit = async (foundVehicle, vin, colorId, selectedOptions) => {
  if (foundVehicle) {
    await onVehicleFetch(foundVehicle.year.id, foundVehicle.make.id, foundVehicle.model.id, foundVehicle.trim.id, vin);

    const mappedColors = mapVINColorFetchResult(foundVehicle.colors, foundVehicle.colorsVinVerified, colorId);
    onVehicleColorFetchSuccess(mappedColors);
    onVehicleEngineFetchSuccess(foundVehicle.engines, foundVehicle.enginesVinVerified, 'id');
    onVehicleTransmissionFetchSuccess(foundVehicle.transmissions, foundVehicle.transmissionsVinVerified, 'id');
    onVehicleDrivetrainFetchSuccess(foundVehicle.drivetrains, foundVehicle.drivetrainsVinVerified, 'id');

    if ('genericOptions' in foundVehicle && foundVehicle.genericOptions.length > 0) {
      onVehicleGenericOptionsFetchSuccess(foundVehicle.genericOptions, foundVehicle.optionsVinVerified);
    } else if ('options' in foundVehicle && foundVehicle.options.length > 0) {
      onVehicleOptionsFetchSuccess(foundVehicle.options, foundVehicle.optionsVinVerified, selectedOptions);
    } else {
      await onVehicleOptionsFetch(foundVehicle.trim.id, selectedOptions);
    }

    onStateFieldAnswered('vehicle', 'cadsUsed', foundVehicle.cadsUsed);
    onStateFieldAnswered('vehicle', 'colorsVinVerified', foundVehicle.colorsVinVerified);
    onStateFieldAnswered('vehicle', 'drivetrainsVinVerified', foundVehicle.drivetrainsVinVerified);
    onStateFieldAnswered('vehicle', 'enginesVinVerified', foundVehicle.enginesVinVerified);
    onStateFieldAnswered('vehicle', 'optionsVinVerified', foundVehicle.optionsVinVerified);
    onStateFieldAnswered('vehicle', 'transmissionsVinVerified', foundVehicle.transmissionsVinVerified);
  }
};

export const onVehiclePossibilitiesFetch = async (vin: string): Promise<VehiclePossibility[]> => {
  EventBus.publish('VehiclePossibilitiesFetch', vin);

  const fetchByVin = await fetchVehiclePossibilities(vin);

  const { possibilities } = fetchByVin;
  EventBus.publish('VehiclePossibilitiesSuccess', possibilities);
  return possibilities;
};

export const onVehicleFetch = async (yearId: number, makeId: number, modelId: number, trimId: number, vin: string = '') => {
  try {
    const trimData = await fetchVehicle(yearId, makeId, modelId, trimId);
    const car = {
      vin,
      year: yearId,
      makeId,
      make: trimData.makeName,
      modelId,
      model: trimData.modelName,
      trimId: trimData.trimId,
      trim: trimData.displayName,
      bodyStyle: trimData.bodyStyle,
    };
    onVehicleFetchSuccess(car);
  } catch (error) {
    onVehicleFetchFailure(error);
  }
};

const onVehicleFetchSuccess = ({ vin = '', year = 0, makeId = 0, make = '', modelId = 0, model = '', trimId = 0, trim = '', bodyStyle = '' } = {}): void => {
  if (trimId > 0) {
    // not sure if we need this right now
    // keeping it in bc there's likely a need
    vehicleConfigState.vin = vin;
    vehicleConfigState.yearId = year;
    vehicleConfigState.makeId = makeId;
    vehicleConfigState.make = make;
    vehicleConfigState.modelId = modelId;
    vehicleConfigState.model = model;
    vehicleConfigState.trimId = trimId;
    vehicleConfigState.trim = trim;
    vehicleConfigState.fullVehicleName = `${year} ${make} ${model} ${trim}`;
    vehicleConfigState.bodyStyle = bodyStyle;

    EventBus.publish('VehicleFetchSuccess', {});
  } else {
    onVehicleFetchFailure('No trim data returned');
  }
};

const onVehicleFetchFailure = (error): void => {
  EventBus.publish('VehicleFetchFailure', error);
};

export const onVehicleColorFetch = async (trimId: number, colorId: number) => {
  try {
    const res = await fetchColors(trimId);
    const mappedColors = mapColorFetchResult(res, colorId);
    onVehicleColorFetchSuccess(mappedColors);
  } catch (error) {
    onVehicleColorFetchFailure(error);
  }
};

// color fetch payload is very different from VIN possibilities color payload
const mapColorFetchResult = (colorData, colorId) => {
  return colorData.map((c) => ({
    categoryName: '',
    displayName: c.displayName,
    isDisabled: false,
    isDefault: false,
    isLocked: false,
    isSelected: c.colorId === colorId,
    optionId: c.colorId,
    showInModal: false,
    wasChangedBySystem: false,
    colorHex: mapColorNameToHex(c.displayName),
  }));
};

// TODO: this id field is weird. our VIN possibilities service call converts colorId => id but our color service call
// does not. we should probably just update our code to use what comes back from api since it's consistent
const mapVINColorFetchResult = (colorData, colorsVinVerified = false, selectedColor) => {
  const specLocked = colorsVinVerified || colorData.length === 1;
  const mappedColors = colorData.map((c) => ({
    categoryName: '',
    displayName: c.displayName,
    isDisabled: c.isDisabled,
    isDefault: c.isDefault,
    isLocked: specLocked,
    isSelected: c.id === selectedColor ? true : c.isSelected,
    optionId: c.id,
    showInModal: specLocked,
    wasChangedBySystem: false,
    colorHex: mapColorNameToHex(c.displayName),
  }));
  return mappedColors;
};

const onVehicleColorFetchSuccess = (mappedColors = []): void => {
  vehicleConfigState.availColors = mappedColors;

  if (mappedColors.length === 1) {
    vehicleConfigState.featuredColor = mappedColors[0].displayName;
    vehicleConfigState.featuredColorHex = mappedColors[0].colorHex;
  }

  const selectedColor = mappedColors.find((c) => c.isSelected);

  if (selectedColor) {
    onStateFieldAnswered('vehicle', 'colorId', selectedColor.optionId);
  }

  EventBus.publish('VehicleColorFetchSuccess', vehicleConfigState.availColors);
};

const onVehicleColorFetchFailure = (error): void => {
  EventBus.publish('VehicleEngineFetchFailure', error);
};

export const onVehicleEngineFetch = async (trimId: number) => {
  await fetchEngines(trimId)
    .then((res) => onVehicleEngineFetchSuccess(res))
    .catch((error) => {
      onVehicleEngineFetchFailure(error);
    });
};

// TODO: this idField is weird. our VIN possibilities service call converts engineId => id but our engine service call
// does not. we should probably just update our code to use what comes back from api since it's consistent
const onVehicleEngineFetchSuccess = (engines = [], enginesVinVerified = false, idField = 'engineId'): void => {
  const specLocked = enginesVinVerified || engines.length === 1;
  const mappedEngines = engines.map((e) => ({
    categoryName: '',
    displayName: e.displayName,
    isDisabled: e.isDisabled,
    isDefault: e.isDefault,
    isLocked: specLocked,
    isSelected: e.isSelected,
    optionId: e[idField],
    showInModal: specLocked,
    wasChangedBySystem: false,
  }));
  vehicleConfigState.availEngines = mappedEngines;

  if (mappedEngines.length === 1) {
    vehicleConfigState.featuredEngine = mappedEngines[0].displayName;
    onStateFieldAnswered('vehicle', 'engineId', mappedEngines[0].optionId);
  }

  EventBus.publish('VehicleEngineFetchSuccess', vehicleConfigState.availEngines);
};

const onVehicleEngineFetchFailure = (error): void => {
  EventBus.publish('VehicleEngineFetchFailure', error);
};

export const onVehicleTransmissionFetch = async (trimId: number) => {
  try {
    const res = await fetchTransmissions(trimId);
    onVehicleTransmissionFetchSuccess(res);
  } catch (error) {
    onVehicleTransmissionFetchFailure(error);
  }
};

// TODO: this idField is weird. our VIN possibilities service call converts transmissionId => id but our transmission service call
// does not. we should probably just update our code to use what comes back from api since it's consistent
const onVehicleTransmissionFetchSuccess = (transmissions = [], transmissionsVinVerified = false, idField = 'transmissionId'): void => {
  const specLocked = transmissionsVinVerified || transmissions.length === 1;
  const mappedTransmissions = transmissions.map((t) => ({
    categoryName: '',
    displayName: t.displayName,
    isDisabled: t.isDisabled,
    isDefault: t.isDefault,
    isLocked: specLocked,
    isSelected: t.isSelected,
    optionId: t[idField],
    showInModal: specLocked,
    wasChangedBySystem: false,
  }));
  vehicleConfigState.availTransmissions = mappedTransmissions;

  if (mappedTransmissions.length === 1) {
    vehicleConfigState.featuredTransmission = mappedTransmissions[0].displayName;
    onStateFieldAnswered('vehicle', 'transmissionId', mappedTransmissions[0].optionId);
  }

  EventBus.publish('VehicleTransmissionFetchSuccess', vehicleConfigState.availTransmissions);
};

const onVehicleTransmissionFetchFailure = (error): void => {
  EventBus.publish('VehicleTransmissionFetchFailure', error);
};

export const onVehicleDrivetrainFetch = async (trimId: number) => {
  try {
    const res = await fetchDrivetrains(trimId);
    onVehicleDrivetrainFetchSuccess(res);
  } catch (error) {
    onVehicleDrivetrainFetchFailure(error);
  }
};

// TODO: this idField is weird. our VIN possibilities service call converts drivetrainId => id but our drivetrain service call
// does not. we should probably just update our code to use what comes back from api since it's consistent
const onVehicleDrivetrainFetchSuccess = (drivetrains = [], drivetrainsVinVerified = false, idField = 'drivetrainId'): void => {
  const specLocked = drivetrainsVinVerified || drivetrains.length === 1;
  const mappedDrivetrains = drivetrains.map((d) => ({
    categoryName: '',
    displayName: d.displayName,
    isDisabled: d.isDisabled,
    isDefault: d.isDefault,
    isLocked: specLocked,
    isSelected: d.isSelected,
    optionId: d[idField],
    showInModal: specLocked,
    wasChangedBySystem: false,
  }));
  vehicleConfigState.availDrivetrains = mappedDrivetrains;

  if (mappedDrivetrains.length === 1) {
    vehicleConfigState.featuredDrivetrain = mappedDrivetrains[0].displayName;
    onStateFieldAnswered('vehicle', 'drivetrainId', mappedDrivetrains[0].optionId);
  }

  EventBus.publish('VehicleDrivetrainFetchSuccess', vehicleConfigState.availDrivetrains);
};

export const onVehicleOptionsFetch = async (trimId: number, selectedOptions: number[] = []) => {
  try {
    const res = await fetchOptions(trimId);
    onVehicleOptionsFetchSuccess(res, false, selectedOptions);
  } catch (error) {
    onVehicleOptionsFetchFailure(error);
  }
};

const onVehicleDrivetrainFetchFailure = (error): void => {
  EventBus.publish('VehicleDrivetrainFetchFailure', error);
};

const onVehicleOptionsFetchSuccess = (options = [], optionsVinVerified = false, selectedOptions: number[] = []): void => {
  let mappedOptions = options.map((o) => {
    const inModal = optionsVinVerified || (o.isDefault && !o.hasOptionRelationships);

    return {
      categoryName: o.categoryName,
      displayName: o.displayName,
      isDisabled: o.isDisabled,
      isDefault: o.isDefault,
      isLocked: optionsVinVerified,
      isSelected: o.isSelected,
      optionId: o.optionId,
      showInModal: inModal, // TODO: double check that this logic matches fury
      wasChangedBySystem: false, // TODO: need to handle conflicting options
    };
  });

  // if selectedOptions are provided, override selction
  if (selectedOptions.length > 0) {
    mappedOptions = mappedOptions.map((option) => {
      return {
        ...option,
        isSelected: selectedOptions.indexOf(option.optionId) >= 0,
      };
    });
  }

  vehicleConfigState.availOptions = mappedOptions;

  if (optionsVinVerified) {
    vehicleConfigState.featuredOptions = mappedOptions;
  }

  EventBus.publish('VehicleOptionsFetchSuccess', vehicleConfigState.availOptions);
};

const onVehicleGenericOptionsFetchSuccess = (options = [], optionsVinVerified = false): void => {
  const mappedOptions = options
    .filter((o) => 'genericName' in o)
    .map((o) => ({
      categoryName: o.classification,
      displayName: o.genericName,
      isDisabled: false,
      isDefault: true,
      isLocked: optionsVinVerified,
      isSelected: true,
      optionId: 0,
      showInModal: true, // TODO: need to figure out this logic from fury
      wasChangedBySystem: false, // TODO: need to handle conflicting options
    }));
  vehicleConfigState.availOptions = mappedOptions;

  if (optionsVinVerified) {
    vehicleConfigState.featuredOptions = mappedOptions;
  }

  EventBus.publish('VehicleGenericOptionsFetchSuccess', vehicleConfigState.availColors);
};

const onVehicleOptionsFetchFailure = (error): void => {
  EventBus.publish('VehicleOptionsFetchFailure', error);
};

export const onVehicleOptionsChangesFetch = async (trimId: number, selections = '') => {
  try {
    const res = await fetchOptionsChanges(trimId, selections);
    onVehicleOptionsFetchChangesSuccess(res);
  } catch (error) {
    onVehicleOptionsFetchChangesFailure(error);
  }
};

const onVehicleOptionsFetchChangesSuccess = (options = []) => {
  console.log('option changes', options);
  const mappedOptions = options.map((o) => {
    return {
      isSelected: o.isSelected,
      optionId: o.optionId,
      isDefault: o.isDefault,
      isDefaultConfiguration: o.isDefaultConfiguration,
      isChangeable: o.isChangeable,
      hasOptionRelationships: o.hasOptionRelationships,
      isDisabled: o.isDisabled,
    };
  });
  vehicleConfigState.changedOptions = mappedOptions;

  EventBus.publish('VehicleChangedOptionsFetchSuccess', vehicleConfigState.changedOptions);
};

const onVehicleOptionsFetchChangesFailure = (error): void => {
  EventBus.publish('VehicleOptionsFetchFailure', error);
};

export const onVehicleUserSelected = async (trimId, value) => {
  onVehicleOptionsAnswered('vehicle', 'options', { optionId: value.optionId, isSelected: value.isSelected });
  answerState.vehicle.selectedOptionsState = [...answerState.vehicle.selectedOptionsState, { optionId: value.optionId, isSelected: value.isSelected }];
  console.log('selected options state', answerState.vehicle.selectedOptionsState);
  await onVehicleOptionsChangesFetch(trimId, encodeURIComponent(convertOptionsToRequestString(answerState.vehicle.selectedOptionsState)));

  const filterOptionDiff = vehicleConfigState.changedOptions
    .filter((option) => option.optionId !== value.optionId)
    .map((opt) => {
      return { optionId: opt.optionId, isSelected: opt.isSelected };
    });
  const filterMatch = vehicleConfigState.changedOptions
    .filter((option) => option.optionId === value.optionId)
    .map((opt) => {
      return { optionId: opt.optionId, isSelected: opt.isSelected };
    });

  updateOptions(filterMatch, filterOptionDiff);
};

export const onVehicleUserSelectedBatch = async (trimId, values) => {
  values.map((value) => {
    onVehicleOptionsAnswered('vehicle', 'options', { optionId: value.optionId, isSelected: value.isSelected });
  });

  answerState.vehicle.selectedOptionsState = [...answerState.vehicle.selectedOptionsState, ...values];
  console.log('selected options state', answerState.vehicle.selectedOptionsState);
  await onVehicleOptionsChangesFetch(trimId, encodeURIComponent(convertOptionsToRequestString(answerState.vehicle.selectedOptionsState)));
};

const updateOptions = (match, diff) => {
  // update vehicle config with answered options
  const updateConfigOpt = vehicleConfigState.availOptions.map((configOption) => {
    return { ...configOption, isSelected: answerState.vehicle.apiSelectedOptionIds.includes(configOption.optionId) };
  });
  const options = updateConfigOpt.map((option) => {
    const newOptionSelected = match.find((opt) => opt.optionId === option.optionId) || option;
    const wasChangedBySystem = diff.find((opt) => opt.optionId === newOptionSelected.optionId);

    if (typeof wasChangedBySystem !== 'undefined') {
      return { ...option, isSelected: wasChangedBySystem.isSelected, wasChangedBySystem: true };
    }
    return { ...option, isSelected: newOptionSelected.isSelected, wasChangedBySystem: false };
  });

  // add options that was changed by system to selectedOptions after the item that user selected
  const gatherSelections = [
    ...answerState.vehicle.selectedOptionsState,
    ...options.filter((option) => option.wasChangedBySystem === true).map((option) => ({ optionId: option.optionId, isSelected: option.isSelected })),
  ];

  vehicleConfigState.availOptions = options;
  answerState.vehicle = { ...answerState.vehicle, selectedOptionsState: gatherSelections };
  answerState.vehicle = { ...answerState.vehicle, apiSelectedOptionIds: filterTrueOptions(options) };
  answerState.vehicle = { ...answerState.vehicle, apiUnselectedOptionIds: filterFalseOptions(options) };
};

export const checkIneligibleTed = (allowIneligibleVehicleSubmission: boolean = false) => {
  const { engineId, transmissionId, drivetrainId } = answerState.vehicle;
  const { excludedEngines, excludedTransmissions, excludedDrivetrains } = vehicleConfigState;
  const { isAssignedOffer } = dynamicEntryState;

  if (!allowIneligibleVehicleSubmission || !isAssignedOffer) {
    if (engineId && excludedEngines.includes(engineId)) {
      vehicleConfigState.ineligibleTed = 'engine';
    } else if (transmissionId && excludedTransmissions.includes(transmissionId)) {
      vehicleConfigState.ineligibleTed = 'transmission';
    } else if (drivetrainId && excludedDrivetrains.includes(drivetrainId)) {
      vehicleConfigState.ineligibleTed = 'drivetrain';
    } else {
      vehicleConfigState.ineligibleTed = '';
    }
  } else {
    vehicleConfigState.ineligibleTed = '';
  }
};

const hasSameTrimInStorage = (trimId) => {
  const rawStorage = getAnswersFromLocal();

  return !!(rawStorage && rawStorage.vehicle && Number(rawStorage.vehicle.trimId) === trimId);
};

const updateTedFromAnswers = () => {
  const engine = vehicleConfigState.availEngines.find((e) => e.optionId === answerState.vehicle.engineId);
  if (engine) {
    vehicleConfigState.engineId = answerState.vehicle.engineId;
    vehicleConfigState.availEngines = vehicleConfigState.availEngines.map((e) => {
      e.isSelected = e.optionId === engine.optionId;
      return e;
    });
  }

  const transmission = vehicleConfigState.availTransmissions.find((t) => t.optionId === answerState.vehicle.transmissionId);
  if (transmission) {
    vehicleConfigState.availTransmissions = vehicleConfigState.availTransmissions.map((t) => {
      t.isSelected = t.optionId === transmission.optionId;
      return t;
    });
    vehicleConfigState.transmissionId = answerState.vehicle.transmissionId;
  }

  const drivetrain = vehicleConfigState.availDrivetrains.find((d) => d.optionId === answerState.vehicle.drivetrainId);
  if (drivetrain) {
    vehicleConfigState.availDrivetrains = vehicleConfigState.availDrivetrains.map((d) => {
      d.isSelected = d.optionId === drivetrain.optionId;
      return d;
    });
    vehicleConfigState.drivetrainId = answerState.vehicle.drivetrainId;
  }
};

export default {
  onVehicleConfigInit,
};
